import { Card } from "@/components/ui/Card"
import { formatMoneyShort } from "@/lib/money"
import { cn } from "@/lib/utils"
import { num } from "../format"
import { type ReviewRow, listName } from "../types"
import DocCell from "./DocCell"
import ExcessCell from "./ExcessCell"

interface Props {
  rows: ReviewRow[]
  selectedId: string | null
  actionCounts: Record<string, number>
  showValue: boolean
  cutoff: number
  currency: string
  onSelect: (externalId: string) => void
}

// The agenda: every listed product in this group, one row each, biggest
// excess value first. The room's current row is tinted; the last column
// counts its actions.
export default function ReviewList({ rows, selectedId, actionCounts, showValue, cutoff, currency, onSelect }: Props) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-3 border-b bg-muted/60 px-4 py-2 text-xs font-semibold uppercase tracking-wide">
        <span className="flex-1">Product</span>
        <span className="w-14 text-right">Qty</span>
        {showValue && <span className="w-16 text-right">Value</span>}
        <span className="w-28 text-right">DOC</span>
        <span className="w-20 text-right">Excess</span>
        <span className="w-14 text-right">Actions</span>
      </div>
      {rows.map((row) => {
        const selected = row.external_id === selectedId
        const count = actionCounts[row.external_id] ?? 0
        return (
          <div
            key={row.external_id}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(row.external_id)}
            onKeyDown={(e) => e.key === "Enter" && onSelect(row.external_id)}
            className={cn(
              "flex cursor-pointer items-center gap-3 border-b px-4 py-3 last:border-b-0 hover:bg-muted/50",
              selected && "bg-primary/10 shadow-[inset_3px_0_0_var(--primary)] hover:bg-primary/10",
            )}
          >
            <div className="min-w-0 flex-1">
              <div className={cn("line-clamp-2 text-sm", selected ? "font-semibold" : "font-medium")}>{listName(row)}</div>
              <div className="text-xs text-muted-foreground">#{row.external_id}</div>
            </div>
            <span className="w-14 text-right text-sm tabular-nums">{num(row.stock)}</span>
            {showValue && (
              <span className="w-16 text-right text-sm tabular-nums">{row.value == null ? "—" : formatMoneyShort(row.value, currency)}</span>
            )}
            <DocCell doc={row.doc} incoming={row.incoming} />
            <ExcessCell excess={row.excess} cutoff={cutoff} currency={currency} />
            <span className="w-14 text-right text-sm tabular-nums">
              {count > 0 ? count : <span className="text-muted-foreground">None</span>}
            </span>
          </div>
        )
      })}
    </Card>
  )
}
